import { APP_NAME } from '@foosball/shared';
import { NavLink, Outlet } from 'react-router';

import { useChallengesAwaitingMe } from '../hooks/useChallenges';
import { useAwaitingMe } from '../hooks/useMatches';
import { useAuth } from '../lib/auth-context';

function Tab({
  to,
  label,
  badge,
  badgeLabel,
}: {
  to: string;
  label: string;
  badge?: number;
  badgeLabel?: (count: number) => string;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-2 text-xs font-medium transition ${
          isActive
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
            : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
        }`
      }
    >
      <span className="w-full truncate text-center">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={badgeLabel ? badgeLabel(badge) : `${badge} awaiting confirmation`}
          className="absolute right-1 top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const awaiting = useAwaitingMe();
  const challenges = useChallengesAwaitingMe();

  return (
    <div className="min-h-dvh pb-24">
      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <span className="font-bold tracking-tight">{APP_NAME}</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 dark:text-slate-400">{user?.displayName}</span>
            {user?.role === 'admin' && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                administrator
              </span>
            )}
            <button
              onClick={() => void logout()}
              className="text-sm text-slate-500 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <Outlet />
      </div>

      <footer className="mx-auto max-w-2xl px-4 pb-6 text-center text-xs text-slate-400 dark:text-slate-500">
        A personal project, not run or controlled by OXTS.{' '}
        <NavLink to="/privacy" className="hover:underline">
          Privacy notice
        </NavLink>
      </footer>

      {/* Bottom bar: thumb-reachable, since results are entered standing at the table. */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95"
      >
        <div className="mx-auto flex max-w-2xl gap-1 px-3 py-2">
          <Tab to="/" label="Home" />
          <Tab to="/matches/new" label="Submit" />
          <Tab to="/confirmations" label="Confirm" badge={awaiting.data?.count} />
          <Tab
            to="/challenges"
            label="Challenge"
            badge={challenges.data?.count}
            badgeLabel={(count) => `${count} challenge${count === 1 ? '' : 's'} awaiting your answer`}
          />
          <Tab to="/matches" label="History" />
          {user?.role === 'admin' && <Tab to="/admin" label="Admin" />}
        </div>
      </nav>
    </div>
  );
}
